// Route tree. Public: /auth/login, /auth/callback, /maintenance, /onboarding/*
// (web onboarding, online only — features/onboarding). Everything
// else sits inside RequireAuth → ErpProvider (me + config) → AppShell, with
// each module's routes under a pathless parent whose handle carries the
// module gate; ContentGate turns any refused gate into <Forbidden/>. An owner
// whose business identity has no name yet is asked «ما اسمك؟» once (OwnerNameGate).
// On an intranet node the whole tree sits under the system module's
// NodeLicenceGate, which adds the public first-run pages BEFORE sign-in:
// /activate (unlicensed node) and /setup (no owner yet). Cloud never probes.

import { useContext, useEffect } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { BrandMark } from '@/components/BrandMark';
import { ErrorCard } from '@/components/ErrorCard';
import { AppShell } from './AppShell';
import { AuthCallback } from './AuthCallback';
import { ErpProvider } from './erp-context';
import { HOME_PATH, LandingContext } from './Forbidden';
import { LoginScreen } from './LoginScreen';
import { Maintenance } from './Maintenance';
import type { ErpModule, ErpRouteHandle } from './module-types';
import { MODULES } from './modules';
import { NoBusinessGate } from './NoBusinessGate';
import { NotFound } from './NotFound';
import { OwnerNameGate } from './OwnerNameGate';
import { RequireAuth } from './RequireAuth';
import { lockStore } from '@/lib/idle-lock';
import { moduleGate } from './gates';
import { withNodeLicenceGate } from '@/features/system/NodeLicenceGate';
import { onboardingRoutes } from '@/features/onboarding';

/** React Router v7 behaviours opted into now (silences the upgrade warnings). */
export const ROUTER_FUTURE = {
  v7_relativeSplatPath: true,
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_skipActionErrorRevalidation: true,
} as const;
export const PROVIDER_FUTURE = { v7_startTransition: true } as const;

/** /lock — lock now (system/lock--idle) and stay on the home page under the lock. */
function LockRoute() {
  useEffect(() => lockStore.lock('manual'), []);
  return <Navigate to={HOME_PATH} replace />;
}

/**
 * «/» — the person's landing: «اليوم» when it is a visible rail row, else the
 * first row of the first rail entry (a hotel with erp.hotel on lands on
 * /hotel/rooms/tape — «الحجوزات» took «المواعيد»'s place). The shell provides
 * the landing from the visible rail (LandingContext).
 */
function IndexRoute() {
  const landing = useContext(LandingContext);
  return <Navigate to={landing?.to ?? HOME_PATH} replace />;
}

function SessionFallback({ loading, error, retry }: { loading: boolean; error: unknown; retry: () => void }) {
  if (loading || !error) {
    return (
      <div className="e-center" data-testid="session-loading">
        <BrandMark mode="loading" size={72} />
        <span className="sr-only">جارٍ التحميل</span>
      </div>
    );
  }
  return (
    <div className="e-center">
      <ErrorCard error={error} onRetry={retry} />
    </div>
  );
}

export function buildRoutes(modules: readonly ErpModule[] = MODULES): RouteObject[] {
  const moduleRoutes: RouteObject[] = modules.map((m) => ({
    id: `module:${m.key}`,
    handle: { gate: moduleGate(m), moduleKey: m.key } satisfies ErpRouteHandle,
    children: m.routes,
  }));
  return withNodeLicenceGate([
    { path: '/auth/login', element: <LoginScreen /> },
    // Web onboarding (online): a new business account, before any session.
    ...onboardingRoutes,
    { path: '/auth/callback', element: <AuthCallback /> },
    { path: '/maintenance', element: <Maintenance /> },
    {
      element: (
        <RequireAuth>
          <NoBusinessGate>
            <ErpProvider fallback={(p) => <SessionFallback {...p} />}>
              <OwnerNameGate>
                <AppShell modules={modules} />
              </OwnerNameGate>
            </ErpProvider>
          </NoBusinessGate>
        </RequireAuth>
      ),
      children: [
        { index: true, element: <IndexRoute /> },
        { path: '/lock', element: <LockRoute /> },
        ...moduleRoutes,
        { path: '*', element: <NotFound /> },
      ],
    },
  ]);
}
